import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_ROBOT_RETURN_LOCATION,
  DEPLOYMENT_RETURN_REASON_LABELS,
  DeploymentReturnReason,
  MaintenanceType,
  ROBOT_RETURN_LOCATION_CHOICES,
  ROBOT_RETURN_LOCATION_LABELS,
  UserRole,
  createsReturnMaintenanceOrder,
  getHolidayName,
  isKoreanHoliday,
  type ActiveCustomerRowDto,
  type RobotReturnDisposition,
  type RobotReturnLocation,
} from '@bhops/shared';
import { activeCustomersApi } from '@/api/active-customers';
import { extractApiError } from '@/api/client';
import { Button, Input, Select } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Icon } from '@/components/Icon';
import { MapUrlIcon } from '@/components/MapUrlIcon';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
// 추가수정 2차(26-07-27) 정정2 — 이 화면의 날짜는 저장·표시를 **한 기준(KST)**
// 으로 통일한다. UTC 자르기 / ymdToIso(로컬 자정 → 전날 15:00Z) 를 섞어 쓰던
// 것이 회수일이 하루씩 밀리는 원인이었다(아래 프리필 주석 참조).
import { kstYmd, toYmdInput } from '@/lib/format-date';

// S3 Phase 3.4 (spec §3.5) — 활성 고객사 관리 탭.
//
// 현장에 나가 있는(DEPLOYED·OTHER — ROBOT_OUT_IN_FIELD_STATUSES) 로봇을 보유한 고객사 roster. 금액/계약 정보 없는 운영/
// 유지보수 뷰. 각 행에서:
//   - 유지보수 등록: 대상 로봇 + 작업 type(부품 교체/펌웨어 업데이트/로봇 교체)
//     + 선택적 예정일 → POST /active-customers/:id/maintenance. ROBOT_REPLACE 는
//     백엔드가 제조팀에 ROBOT_REPLACEMENT_REQUESTED 알람을 발행한다(응답의
//     replacementAlarmSent 로 확인).
//   - 회수일 확정(returnPlanned 행만): 회수일 입력 → PATCH .../return-date. ⚠
//     회수 예정 마커 표시.
//
// 보류(후속): 예방정비 자동 제안(spec "자동 생성")은 본 Phase 범위 밖 — 수동
// 등록만. 백엔드도 동일.

// 추가수정 2차(26-07-27) 정정 — 공휴일 경고(결정 ③A) 확산 누락분.
// 이 화면의 날짜 칸은 Field 로 감싸지 않은 인라인 입력이라 hint 자리가 없다.
// 문구·데이터(packages/shared 의 KOREAN_HOLIDAYS)는 가스 설비 발주 창 ·
// 배치/회수 창과 동일하게 맞춰 화면마다 표현이 갈리지 않게 한다.
function HolidayHint({ ymd }: { ymd: string }) {
  if (!isKoreanHoliday(ymd)) return null;
  return <span className="kuun-caption">공휴일 ({getHolidayName(ymd)}) 입니다</span>;
}

/*
 * 회수일 프리필은 `toYmdInput`(= 정본 `kstYmdHyphen` + `''` 폴백).
 *
 * 왜 KST 환산이 필요했나(추가수정 2차 26-07-27 정정2) — 이 화면의 회수일은
 * **저장과 재표시가 서로 다른 시간대 규칙**을 썼다:
 *   · 저장   `ymdToIso(returnYmd)` → 브라우저 로컬(=한국) 자정 → **전날 15:00Z**
 *   · 재표시 UTC 자르기            → **전날 날짜**
 * 그래서 8/20 을 확정하면 화면에 8/19 로 보이고, 화면을 다시 열면 입력칸에도
 * 8/19 가 프리필돼 [회수일 확정]을 누를 때마다 하루씩 앞으로 밀려 **저장됐다**
 * (표시 결함이 아니라 데이터가 실제로 바뀌었다).
 *
 * 이미 그렇게 밀려 저장된 기존 행(전날 15:00Z)이 DB 에 남아 있으므로, 프리필의
 * KST 환산은 저장 형식을 고친 뒤에도 계속 필요하다 — 그 행도 한국 날짜로 읽히고
 * 다음 확정에서 정상 형식으로 자연히 교정된다.
 *
 * 한국시간 통일 5차(26-08-14) — 종전엔 이 파일이 `kstYmdInput` 지역 어댑터
 * (`kstYmd(v)?.replace(/\./g,'-')`)를 갖고 있었다. 5차에서 `toYmdInput` 이 KST
 * 정본으로 옮겨져 두 이름이 같은 뜻이 됐으므로 지역 사본을 지운다(동작 불변).
 * 정본(`kstYmdHyphen`)이 하이픈 표기를 직접 내주므로 표기 변환 한 줄도 불필요하다.
 */

const MAINTENANCE_LABEL: Record<MaintenanceType, string> = {
  [MaintenanceType.PART_REPLACE]: '부품 교체',
  [MaintenanceType.FIRMWARE_UPDATE]: '펌웨어 업데이트',
  [MaintenanceType.ROBOT_REPLACE]: '로봇 교체',
};

// round6 §1-5 — 카드 그리드 반응형 열 수(3 / 2 / 1).
function useGridCols(): number {
  const calc = () => (window.innerWidth >= 1280 ? 3 : window.innerWidth >= 900 ? 2 : 1);
  const [cols, setCols] = useState<number>(() => (typeof window === 'undefined' ? 3 : calc()));
  useEffect(() => {
    const onResize = () => setCols(calc());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return cols;
}

export function ActiveCustomersPage() {
  const role = useAuthStore((s) => s.user?.role);
  // 유지보수 등록 / 회수일 확정은 field/mfg/sales manager + admin. 백엔드가
  // 한 번 더 가드하므로 UI 가드는 UX 용 (VIEWER 는 읽기만).
  const canWrite =
    role === UserRole.ADMIN ||
    role === UserRole.MANAGER ||
    role === UserRole.SALES_MANAGER ||
    role === UserRole.MFG_MANAGER ||
    role === UserRole.FIELD_MANAGER;

  const gridCols = useGridCols();

  const listQ = useQuery({
    queryKey: ['active-customers'],
    queryFn: () => activeCustomersApi.list(),
    staleTime: 30_000,
  });

  const rows = listQ.data?.items ?? [];

  // round9 (요구 5) — 활성 고객사가 늘어나면 카드 나열만으로 탐색이 어려워
  // 검색/모델 필터를 도입한다. 백엔드가 이미 전량(scoped 배치 로봇 보유
  // 고객사)을 반환하고 목록 규모가 본질적으로 작아(수십~저수백) 클라이언트
  // 사이드 필터로 충분하다.
  const [searchInput, setSearchInput] = useState('');
  const [modelFilter, setModelFilter] = useState('');

  // 현재 로드된 행에 실제로 존재하는 모델만 옵션으로 제공(추가 페치 불필요).
  const modelOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      for (const g of r.robotsByModel) {
        if (!map.has(g.modelCode)) map.set(g.modelCode, g.modelDisplayName);
      }
    }
    return Array.from(map.entries());
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return rows.filter((r) => {
      if (modelFilter && !r.robotsByModel.some((g) => g.modelCode === modelFilter)) return false;
      if (!q) return true;
      return (
        r.displayName.toLowerCase().includes(q) || (r.contactName ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, searchInput, modelFilter]);

  const filterActive = searchInput.trim() !== '' || modelFilter !== '';

  // 개선계획 §7 — 카드가 항상 펼쳐져 있어 고객사가 늘면 스크롤만 길어졌다.
  // 기본 접힘 + 다중 펼침. 회수 예정 건은 지금 처리해야 할 일이라 처음부터 펼친다.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());
  // rows 는 매 렌더 새 배열이라 의존성으로 쓰면 루프가 된다 — 페치 결과에만 반응.
  useEffect(() => {
    const items = listQ.data?.items;
    if (!items) return;
    setExpandedIds(new Set(items.filter((r) => r.returnPlanned).map((r) => r.customerId)));
  }, [listQ.data]);

  function toggleExpanded(customerId: string): void {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  }

  return (
    <div style={page}>
      <header style={headerRow}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20 }}>활성 고객사</h1>
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
          {listQ.isLoading
            ? '불러오는 중…'
            : filterActive
              ? `필터 ${filteredRows.length} / 총 ${rows.length}곳`
              : `총 ${rows.length}곳`}
        </div>
      </header>

      {!listQ.isLoading && !listQ.isError && rows.length > 0 && (
        <div style={toolbar}>
          <input
            aria-label="상호나 담당자로 검색"
            placeholder="상호나 담당자로 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={toolInput}
          />
          <select
            aria-label="모델 필터"
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            style={toolSelect}
          >
            <option value="">모델 전체</option>
            {modelOptions.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <Button
              variant="ghost"
              onClick={() => setExpandedIds(new Set(filteredRows.map((r) => r.customerId)))}
            >
              모두 펼치기
            </Button>
            <Button variant="ghost" onClick={() => setExpandedIds(new Set())}>
              모두 접기
            </Button>
          </div>
        </div>
      )}

      {listQ.isError && <div style={errorBox}>{extractApiError(listQ.error)}</div>}

      {!listQ.isLoading && !listQ.isError && rows.length === 0 && (
        <div style={emptyBox}>현장에 나가 있는 로봇이 있는 고객사가 없습니다.</div>
      )}

      {!listQ.isLoading && !listQ.isError && rows.length > 0 && filteredRows.length === 0 && (
        <div style={emptyBox}>조건에 맞는 고객사가 없습니다.</div>
      )}

      {/* round6 §1-5 — 카드 그리드 3열(1280px 미만 2열, 900px 미만 1열). */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
          gap: 12,
          alignItems: 'start',
        }}
      >
        {filteredRows.map((row) => (
          <ActiveCustomerCard
            key={row.customerId}
            row={row}
            canWrite={canWrite}
            expanded={expandedIds.has(row.customerId)}
            onToggle={() => toggleExpanded(row.customerId)}
          />
        ))}
      </div>
    </div>
  );
}

function ActiveCustomerCard({
  row,
  canWrite,
  expanded,
  onToggle,
}: {
  row: ActiveCustomerRowDto;
  canWrite: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  // R2 후속(워크플로 M7) — 폐기(RETIRE)는 백엔드 INV-05 상 ADMIN 전용. 비ADMIN
  // 매니저에게 '폐기' 옵션을 노출하면 항상 403 이므로 ADMIN 에게만 옵션을 보인다.
  const isAdmin = useAuthStore((s) => s.user?.role === UserRole.ADMIN);

  // 유지보수 폼 로컬 상태 (행마다 독립).
  const firstRobotId = row.robotsByModel[0]?.robots[0]?.robotId ?? '';
  const [robotId, setRobotId] = useState(firstRobotId);
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType>(
    MaintenanceType.PART_REPLACE,
  );
  const [scheduledYmd, setScheduledYmd] = useState('');
  const [note, setNote] = useState('');

  // 회수일 확정 로컬 상태.
  // 추가수정 2차(26-07-27) 정정2 — 프리필을 KST 기준으로. 값이 없으면 ''
  // (controlled date input 안전).
  const [returnYmd, setReturnYmd] = useState(() => toYmdInput(row.returnScheduledAt));

  // R2 #7 (plan G-3.4) — 회수 완료 로컬 상태(대상 로봇 + 정비/폐기 처리).
  // 개선계획 §7 — + 회수 사유. 고객사 종료가 뒤따르는 되돌리기 어려운 조작이라
  // 확인 창을 한 단계 둔다.
  const [completeRobotId, setCompleteRobotId] = useState(firstRobotId);
  const [disposition, setDisposition] = useState<RobotReturnDisposition>('MAINTENANCE');
  // 개선 9건 차수1(26-08-04) 결정 ①A — 회수 장소. 로봇 상세의 회수 창과 같은
  // 선택지·같은 기본값(A/S센터)을 쓴다.
  const [returnLocation, setReturnLocation] = useState<RobotReturnLocation>(
    DEFAULT_ROBOT_RETURN_LOCATION,
  );
  const [returnReason, setReturnReason] = useState<DeploymentReturnReason>(
    DeploymentReturnReason.CONTRACT_END,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 장소는 정비 입고에만 의미가 있다(폐기는 창고 고정) — 서버 규칙과 같다.
  const showLocation = disposition === 'MAINTENANCE';

  const maintenanceM = useMutation({
    mutationFn: () =>
      activeCustomersApi.createMaintenance(row.customerId, {
        robotId,
        maintenanceType,
        // 추가수정 2차(26-07-27) 정정2 — date input 이 이미 KST 기준 맨 날짜
        // ("yyyy-mm-dd")를 들고 있으므로 그대로 보낸다. 서버 계약 확인:
        // CreateMaintenanceDto.scheduledAt 은 @IsISO8601() 문자열이고 맨 날짜도
        // 통과하며(ISO 8601 date), 내부에서 new Date() 로 UTC 자정에 저장된다.
        // 이전의 ymdToIso 는 로컬 자정(=전날 15:00Z)으로 보내서, 제조팀 교체
        // 요청 알람이 그 값을 slice(0,10) 로 읽는 자리와 캘린더 칸이 하루 이른
        // 날짜를 집었다.
        scheduledAt: scheduledYmd || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['active-customers'] });
      // ROBOT_REPLACE 면 제조팀 알람 발행 여부를 토스트로 surface.
      if (maintenanceType === MaintenanceType.ROBOT_REPLACE) {
        toast.success(
          res.replacementAlarmSent
            ? `로봇 교체 유지보수를 등록하고 제조팀에 교체 요청 알림을 보냈습니다. 오더 번호는 ${res.serviceOrder.humanId}입니다.`
            : `로봇 교체 유지보수를 등록했지만 제조팀에 알림을 보내지 못했습니다. 오더 번호는 ${res.serviceOrder.humanId}입니다.`,
          '유지보수 등록',
        );
      } else {
        toast.success(
          `${MAINTENANCE_LABEL[maintenanceType]} 유지보수를 등록했습니다. 오더 번호는 ${res.serviceOrder.humanId}입니다.`,
          '유지보수 등록',
        );
      }
      setNote('');
      setScheduledYmd('');
    },
    onError: (err) => toast.error(extractApiError(err), '유지보수 등록 실패'),
  });

  const returnDateM = useMutation({
    mutationFn: (next: string | null) => activeCustomersApi.setReturnDate(row.customerId, next),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['active-customers'] });
      toast.success(
        res.returnScheduledAt ? '회수일을 확정했습니다.' : '회수일 확정을 해제했습니다.',
        '회수일 확정',
      );
    },
    onError: (err) => toast.error(extractApiError(err), '회수일 확정 실패'),
  });

  // R2 #7 (plan G-3.4) — 회수 완료: 로봇 정비/폐기 전이 + 마지막 로봇이면 고객사 종료.
  const completeReturnM = useMutation({
    mutationFn: () =>
      activeCustomersApi.completeReturn(row.customerId, {
        robotId: completeRobotId,
        disposition,
        returnReason,
        // 개선 9건 차수1(26-08-04) — 화면이 물었을 때만 보낸다(폐기면 미전송).
        returnLocation: showLocation ? returnLocation : undefined,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['active-customers'] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      setConfirmOpen(false);
      toast.success(
        `${res.robotStatus === 'RETIRED' ? '폐기' : '정비 입고'} 처리했습니다.` +
          (res.maintenanceOrderHumanId
            ? ` 정비 오더도 만들었습니다. 오더 번호는 ${res.maintenanceOrderHumanId}입니다.`
            : '') +
          (res.customerRetired
            ? ' 고객사가 종료되었습니다. 잘못 종료했다면 사업팀에 종료 취소를 요청해 주세요.'
            : ''),
        '회수 완료',
      );
    },
    onError: (err) => toast.error(extractApiError(err), '회수 완료 실패'),
  });

  const hasRobots = row.robotsByModel.length > 0;

  return (
    <section style={card} data-testid={`active-customer-${row.customerId}`}>
      {/* 접힘 상태에서 한 줄로 읽히는 요약 — 상호 / 모델×대수 / 담당 / 회수 예정. */}
      <button
        type="button"
        style={cardToggle}
        aria-expanded={expanded}
        aria-label={`${row.displayName} 카드 ${expanded ? '접기' : '펼치기'}`}
        onClick={onToggle}
      >
        <Icon
          name="arrow-right"
          size={14}
          style={{ transform: expanded ? 'rotate(90deg)' : undefined }}
        />
        <span style={cardTitle} title={row.displayName}>
          {row.displayName}
        </span>
        {row.robotsByModel.map((g) => (
          <span key={g.modelCode} style={modelBadge} title={g.modelDisplayName}>
            {g.modelDisplayName}×{g.count}
          </span>
        ))}
        <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
          담당 {row.contactName ?? '미입력'}
        </span>
        {row.returnPlanned && (
          <span style={returnFlag} title="사업팀이 회수 예정으로 바꿨습니다">
            <Icon name="alert" size={14} aria-hidden /> 회수 예정
          </span>
        )}
      </button>

      {expanded && (
        <>
          <div style={cardHead}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <MapUrlIcon url={row.addressMapUrl} />
              <Link to={`/customers/${row.customerId}`} style={detailLink}>
                고객사 상세 열기
              </Link>
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
              {/* 추가수정 2차(26-07-27) 정정2 — 같은 값을 고객사 상세("도입일") ·
                  인프라 목록 · 매장 상세는 이미 KST 점 표기로 그린다. 여기만 UTC
                  자르기면 화면마다 하루 어긋난다(가스 설비 발주 창에서 로컬 자정
                  으로 저장된 기존 완료일이 특히 그렇다). */}
              설치 완료 {kstYmd(row.installCompletedAt) ?? '–'}
            </div>
          </div>

          <div style={contactRow}>
            담당:{' '}
            {row.contactName ? (
              <>
                {row.contactName}
                {row.contactPosition ? ` · ${row.contactPosition}` : ''}
                {row.contactPhone ? ` · ${row.contactPhone}` : ''}
              </>
            ) : (
              <span style={{ color: 'var(--fg-subtle)' }}>미입력</span>
            )}
          </div>

          {canWrite && (
            <div style={actionsWrap}>
              {/* 유지보수 등록 */}
              <div style={maintRow}>
                <span style={actionLabel}>유지보수 등록</span>
                <Select
                  aria-label="대상 로봇"
                  value={robotId}
                  onChange={(e) => setRobotId(e.target.value)}
                  style={{ minWidth: 150 }}
                  disabled={!hasRobots}
                >
                  {row.robotsByModel.flatMap((g) =>
                    g.robots.map((r) => (
                      <option key={r.robotId} value={r.robotId}>
                        {g.modelDisplayName} · {r.serialShort ?? r.serialLong ?? r.robotId}
                      </option>
                    )),
                  )}
                </Select>
                <Select
                  aria-label="작업 종류"
                  value={maintenanceType}
                  onChange={(e) => setMaintenanceType(e.target.value as MaintenanceType)}
                  style={{ minWidth: 140 }}
                >
                  {(Object.keys(MAINTENANCE_LABEL) as MaintenanceType[]).map((mt) => (
                    <option key={mt} value={mt}>
                      {MAINTENANCE_LABEL[mt]}
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label="유지보수 예정일"
                  type="date"
                  value={scheduledYmd}
                  onChange={(e) => setScheduledYmd(e.target.value)}
                  style={{ width: 150 }}
                />
                <HolidayHint ymd={scheduledYmd} />
                <Input
                  aria-label="메모"
                  type="text"
                  placeholder="메모(선택)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  style={{ width: 180 }}
                />
                <Button
                  variant={maintenanceType === MaintenanceType.ROBOT_REPLACE ? 'danger' : 'primary'}
                  disabled={!hasRobots || !robotId || maintenanceM.isPending}
                  onClick={() => maintenanceM.mutate()}
                >
                  {maintenanceType === MaintenanceType.ROBOT_REPLACE
                    ? '교체 등록하고 제조팀에 알리기'
                    : '등록'}
                </Button>
              </div>

              {/* 회수일 확정 — 회수 예정 행만 */}
              {row.returnPlanned && (
                <div style={returnRow}>
                  <span style={actionLabel}>회수일 확정</span>
                  <Input
                    aria-label="회수일"
                    type="date"
                    value={returnYmd}
                    onChange={(e) => setReturnYmd(e.target.value)}
                    style={{ width: 150 }}
                  />
                  <HolidayHint ymd={returnYmd} />
                  <Button
                    variant="secondary"
                    disabled={!returnYmd || returnDateM.isPending}
                    // 추가수정 2차(26-07-27) 정정2 — 입력칸이 들고 있는 KST 맨
                    // 날짜를 그대로 보낸다(가스 설비 발주 창의 kstTodayYmd 와 같은
                    // 원칙). 서버 계약 확인: SetReturnDateDto.returnScheduledAt 은
                    // @IsISO8601() 문자열로 맨 날짜를 허용하고, 서비스가
                    // new Date(...) → UTC 자정으로 저장한 뒤 ISO 로 되돌려준다.
                    // 그 ISO 를 아래 kstYmd 가 다시 읽으면 같은 한국 날짜가 나와
                    // 왕복이 안정된다(PC 타임존과 무관).
                    onClick={() => returnDateM.mutate(returnYmd)}
                  >
                    회수일 확정
                  </Button>
                  {row.returnScheduledAt && (
                    <Button
                      variant="ghost"
                      disabled={returnDateM.isPending}
                      onClick={() => {
                        setReturnYmd('');
                        returnDateM.mutate(null);
                      }}
                    >
                      확정 해제
                    </Button>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                    {/* 추가수정 2차(26-07-27) 정정2 — 저장값(UTC 자정 ISO)과 밀려
                        저장된 기존 값(전날 15:00Z) 모두 한국 날짜로 읽히도록 KST
                        기준. 표기는 목록 표준인 점 구분(결정 ②A). */}
                    확정된 회수일: {kstYmd(row.returnScheduledAt) ?? '–'}
                  </span>
                </div>
              )}

              {/* R2 #7 (plan G-3.4) — 회수 완료: 대상 로봇을 정비(IN_AS)/폐기(RETIRED)로
              전이하고, 그 고객사의 마지막 운영 로봇이면 고객사를 종료(retired)한다.
              개선계획 §7 — returnPlanned 게이팅 제거: 현장에서 이미 걷어온 로봇을
              사업팀의 '회수 예정' 전환을 기다리느라 처리 못 하던 문제. */}
              {hasRobots && (
                <div style={returnRow}>
                  <span style={actionLabel}>회수 완료</span>
                  <Select
                    aria-label="회수 완료 대상 로봇"
                    value={completeRobotId}
                    onChange={(e) => setCompleteRobotId(e.target.value)}
                    style={{ minWidth: 150 }}
                  >
                    {row.robotsByModel.flatMap((g) =>
                      g.robots.map((r) => (
                        <option key={r.robotId} value={r.robotId}>
                          {g.modelDisplayName} · {r.serialShort ?? r.serialLong ?? r.robotId}
                        </option>
                      )),
                    )}
                  </Select>
                  <Select
                    aria-label="회수 사유"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value as DeploymentReturnReason)}
                    style={{ minWidth: 120 }}
                  >
                    {(Object.keys(DEPLOYMENT_RETURN_REASON_LABELS) as DeploymentReturnReason[]).map(
                      (r) => (
                        <option key={r} value={r}>
                          {DEPLOYMENT_RETURN_REASON_LABELS[r]}
                        </option>
                      ),
                    )}
                  </Select>
                  <Select
                    aria-label="회수 처리"
                    value={disposition}
                    onChange={(e) => setDisposition(e.target.value as RobotReturnDisposition)}
                    style={{ minWidth: 120 }}
                  >
                    <option value="MAINTENANCE">정비 입고</option>
                    {isAdmin && <option value="RETIRE">폐기</option>}
                  </Select>
                  {/* 개선 9건 차수1(26-08-04) 결정 ①A — 회수 장소. 선택지는 로봇
                      상세의 회수 창과 **같은 라벨 맵**을 순회해 만든다. */}
                  {showLocation && (
                    <Select
                      aria-label="회수 장소"
                      value={returnLocation}
                      onChange={(e) => setReturnLocation(e.target.value as RobotReturnLocation)}
                      style={{ minWidth: 110 }}
                    >
                      {ROBOT_RETURN_LOCATION_CHOICES.map((loc) => (
                        <option key={loc} value={loc}>
                          {ROBOT_RETURN_LOCATION_LABELS[loc]}
                        </option>
                      ))}
                    </Select>
                  )}
                  <Button
                    variant={disposition === 'RETIRE' ? 'danger' : 'secondary'}
                    disabled={!completeRobotId || completeReturnM.isPending}
                    onClick={() => setConfirmOpen(true)}
                  >
                    회수 완료
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="회수 완료"
        message={
          `"${row.displayName}"의 로봇 1대를 ${disposition === 'RETIRE' ? '폐기' : '정비 입고'} 처리합니다.\n` +
          `회수 사유: ${DEPLOYMENT_RETURN_REASON_LABELS[returnReason]}\n` +
          // 개선 9건 차수1(26-08-04) 결정 ①A — 확인 창이 **무엇이 저장될지** 말한다.
          // 연구소·창고면 정비 지시가 안 만들어지는 것까지 여기서 알린다(회수 창의
          // 안내문과 같은 문장 — 두 경로가 다른 말을 하면 안 된다).
          (showLocation
            ? `회수 장소: ${ROBOT_RETURN_LOCATION_LABELS[returnLocation]}\n` +
              (createsReturnMaintenanceOrder(returnLocation)
                ? ''
                : '정비 지시가 만들어지지 않습니다. 분석이나 보관이 끝나면 로봇 편집에서 상태를 정리해 주세요.\n')
            : '') +
          `\n이 고객사의 마지막 운영 로봇이면 고객사가 종료 처리됩니다.`
        }
        confirmLabel="회수 완료"
        danger={disposition === 'RETIRE'}
        busy={completeReturnM.isPending}
        onConfirm={() => completeReturnM.mutate()}
        onCancel={() => {
          if (completeReturnM.isPending) return;
          setConfirmOpen(false);
        }}
      />
    </section>
  );
}

const page: CSSProperties = { padding: 20, maxWidth: 1560 }; // round6 §1-5 — 3열 그리드 폭
const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  marginBottom: 16,
};
// round9 (요구 5) — CustomerMasterPage 툴바(toolInput/toolSelect)와 동일 시각 패턴.
const toolbar: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: 12,
};
const toolInput: CSSProperties = {
  flex: '0 1 260px',
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
const toolSelect: CSSProperties = {
  height: 32,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
};
const errorBox: CSSProperties = {
  padding: 12,
  borderRadius: 'var(--r-card)',
  background: 'var(--red-50)',
  color: 'var(--fg-danger)',
  marginBottom: 12,
  fontSize: 13,
};
const emptyBox: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
};
const card: CSSProperties = {
  border: '1px solid var(--border-card)',
  borderRadius: 'var(--r-card)',
  padding: 14,
  background: 'var(--bg-surface)',
};
const cardToggle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  width: '100%',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--fg-default)',
  textAlign: 'left',
  cursor: 'pointer',
};
// 긴 상호가 배지/담당을 밀어내지 않도록 한 줄 말줄임(전체 값은 title 로).
const cardTitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  maxWidth: 260,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const detailLink: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-brand)',
  textDecoration: 'none',
  fontWeight: 600,
};
const cardHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  marginTop: 8,
};
const contactRow: CSSProperties = { marginTop: 6, fontSize: 13, color: 'var(--fg-default)' };
const modelBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--blue-50)',
  color: 'var(--blue-800)',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'var(--font-mono)',
};
const returnFlag: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  fontSize: 12,
  fontWeight: 600,
};
const actionsWrap: CSSProperties = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px solid var(--border-subtle)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const maintRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const returnRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const actionLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  minWidth: 78,
};
